import { useEffect, useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';

import {
  requirementErrorMessages,
  requirementTitles,
} from './helpers/requirementTitles.helper';
import {
  isStepComplete,
  TDocumentRequirement,
} from './helpers/stepCompletion.helper';
import { StepValue } from './StepContext';
import { TGetDocumentsResponse } from '../../../api/lendingApi/lendingApiQueries';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { trackDocumentUploadClicked } from '../UploadButton/UploadButton.tracking.helper';

const REQUIREMENT_TYPE_TO_TRACKING_DOC_TYPE: Record<
  string,
  Parameters<typeof trackDocumentUploadClicked>[0]
> = {
  aged_debtor_report: 'aged debtor report',
  bank_statement: 'bank statement',
  business_plan: 'business plan',
  company_accounts: 'business accounts',
  management_accounts: 'management accounts',
  vat_return: 'vat return',
};

type Args = {
  requirements: TDocumentRequirement[];
  documents: TGetDocumentsResponse | undefined;
  refetch: () => void;
  stateKey: string | undefined;
  onDecision: () => Promise<unknown> | unknown;
  isDeciding: boolean;
  isRefetchingOffers: boolean;
  isReady: boolean;
};

export const useTradingHistorySteps = ({
  requirements,
  documents,
  refetch,
  stateKey,
  onDecision,
  isDeciding,
  isRefetchingOffers,
  isReady,
}: Args) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [attemptedSteps, setAttemptedSteps] = useState<Set<number>>(new Set());
  const [directUploadStepIndex, setDirectUploadStepIndex] = useState<
    number | null
  >(null);
  const [showSummary, setShowSummary] = useState(false);
  const initialized = useRef(false);

  // Refetch documents on mount to ensure fresh data
  useEffect(() => {
    if (stateKey) {
      refetch();
    }
  }, [stateKey, refetch]);

  // Set active step to first incomplete step on initial load only
  useEffect(() => {
    if (!isReady || requirements.length === 0 || initialized.current) return;
    initialized.current = true;

    const firstIncomplete = requirements.findIndex(
      (req) => !isStepComplete(req, documents),
    );
    if (firstIncomplete === -1) {
      setShowSummary(true);
    } else {
      setActiveStepIndex(firstIncomplete);
    }
  }, [isReady, requirements, documents]);

  const onSubmit = async () => {
    tracking.ampli.tradingHistorySubmitted({
      'Page version': 'v3',
      ...getAmplitudeDefaultEventProperties(),
    });
    await onDecision();
  };

  const steps: StepValue[] = requirements.map((requirement, index) => {
    const isLastStep = index === requirements.length - 1;
    const isComplete = isStepComplete(requirement, documents);
    const isActive = !showSummary && index === activeStepIndex;
    const isError = attemptedSteps.has(index) && !isComplete;
    const showDirectUpload = directUploadStepIndex === index;
    const title = requirementTitles(
      requirement.type,
      requirement.quantifier?.value,
    );

    const onContinue = () => {
      const trackingDocType =
        REQUIREMENT_TYPE_TO_TRACKING_DOC_TYPE[requirement.type];
      if (trackingDocType) {
        trackDocumentUploadClicked(trackingDocType);
      }

      setAttemptedSteps((prev) => new Set(prev).add(index));

      if (!isStepComplete(requirement, documents)) {
        return;
      }

      if (isLastStep) {
        setShowSummary(true);
      } else {
        setActiveStepIndex(index + 1);
      }
      setDirectUploadStepIndex(null);
    };

    const onRequestUpload = () => {
      setShowSummary(false);
      setActiveStepIndex(index);
      setDirectUploadStepIndex(index);
    };

    return {
      index,
      requirement,
      title,
      errorMessage: requirementErrorMessages[requirement.type],
      isActive,
      isComplete,
      isError,
      isLastStep,
      showDirectUpload,
      onContinue,
      onRequestUpload,
    };
  });

  return {
    steps,
    showSummary,
    onSubmit,
    isSubmitting: isDeciding || isRefetchingOffers,
  };
};
